import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  GitCompare, Plus, Calendar, TrendingUp, TrendingDown, Minus, 
  Download, User, Clock, AlertCircle, CheckCircle, Loader2,
  ChevronRight, FileText, Info, Lock, History, RotateCcw,
  AlertTriangle, ArrowRight, Shield
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import api from '../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BaselinesPage = () => {
  const { activeSchedule, can } = useProject();

  // Alias for consistency
  const selectedSchedule = activeSchedule;
  
  // State
  const [baselines, setBaselines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Comparison state
  const [selectedBaselineId, setSelectedBaselineId] = useState(null);
  const [compareToBaselineId, setCompareToBaselineId] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBaseline, setNewBaseline] = useState({
    name: '',
    description: '',
    baseline_type: 'secondary'
  });
  
  // Publish preview state
  const [publishPreview, setPublishPreview] = useState(null);
  const [loadingPublishPreview, setLoadingPublishPreview] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishName, setPublishName] = useState('');
  const [publishDescription, setPublishDescription] = useState('');
  
  // Rollback state
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rollbackBaselineId, setRollbackBaselineId] = useState(null);
  const [rollbackName, setRollbackName] = useState('');
  const [rollbackDescription, setRollbackDescription] = useState('');
  const [rollingBack, setRollingBack] = useState(false);
  
  // Load baselines
  const loadBaselines = useCallback(async () => {
    if (!selectedSchedule?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/baselines/schedule/${selectedSchedule.id}`);
      setBaselines(response.data);
    } catch (err) {
      console.error('Failed to load baselines:', err);
      setError('Failed to load baselines');
    } finally {
      setLoading(false);
    }
  }, [selectedSchedule?.id]);
  
  useEffect(() => {
    loadBaselines();
  }, [loadBaselines]);
  
  // Compare baseline against current schedule
  const handleCompareWithCurrent = async (baselineId) => {
    if (!baselineId) return;
    
    setSelectedBaselineId(baselineId);
    setCompareToBaselineId(null);
    setComparing(true);
    setComparison(null);
    
    try {
      const response = await api.get(`/baselines/${baselineId}/compare`);
      setComparison(response.data);
    } catch (err) {
      console.error('Failed to compare baseline:', err);
      setError('Failed to compare baseline');
    } finally {
      setComparing(false);
    }
  };
  
  // Compare two baselines
  const handleCompareBaselines = async () => {
    if (!selectedBaselineId || !compareToBaselineId) return;
    
    setComparing(true);
    setComparison(null);
    
    try {
      const response = await api.get(`/baselines/${selectedBaselineId}/compare-to/${compareToBaselineId}`);
      setComparison(response.data);
    } catch (err) {
      console.error('Failed to compare baselines:', err);
      setError('Failed to compare baselines');
    } finally {
      setComparing(false);
    }
  };
  
  // Create baseline
  const handleCreateBaseline = async () => {
    if (!selectedSchedule?.id || !newBaseline.name.trim()) return;
    
    setCreating(true);
    
    try {
      await api.post('/baselines', {
        name: newBaseline.name.trim(),
        schedule_id: selectedSchedule.id,
        baseline_type: newBaseline.baseline_type,
        description: newBaseline.description.trim() || null
      });
      
      toast.success('Baseline created successfully');
      setCreateDialogOpen(false);
      setNewBaseline({ name: '', description: '', baseline_type: 'secondary' });
      loadBaselines();
    } catch (err) {
      console.error('Failed to create baseline:', err);
      setError(apiErrorMessage(err, 'Failed to create baseline'));
      toast.error(apiErrorMessage(err, 'Failed to create baseline'));
    } finally {
      setCreating(false);
    }
  };
  
  // Load publish preview
  const handleLoadPublishPreview = async () => {
    if (!selectedSchedule?.id) return;
    
    setLoadingPublishPreview(true);
    
    try {
      const response = await api.get(`/baselines/schedule/${selectedSchedule.id}/publish-preview`);
      setPublishPreview(response.data);
      setPublishDialogOpen(true);
      
      // Generate default name
      const today = format(new Date(), 'yyyy-MM-dd');
      setPublishName(`BL-${baselines.length + 1} (${today})`);
    } catch (err) {
      console.error('Failed to load publish preview:', err);
      toast.error('Failed to load change preview');
    } finally {
      setLoadingPublishPreview(false);
    }
  };
  
  // Publish changes as new baseline
  const handlePublish = async () => {
    if (!publishName.trim()) return;
    
    setPublishing(true);
    
    try {
      await api.post('/baselines', {
        name: publishName.trim(),
        schedule_id: selectedSchedule.id,
        baseline_type: 'secondary',
        description: publishDescription.trim() || `Published changes: ${publishPreview?.total_activities_changed || 0} activities modified`
      });
      
      toast.success('Changes published as new baseline');
      setPublishDialogOpen(false);
      setPublishPreview(null);
      setPublishName('');
      setPublishDescription('');
      loadBaselines();
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error(apiErrorMessage(err, 'Failed to publish changes'));
    } finally {
      setPublishing(false);
    }
  };
  
  // Open rollback dialog
  const handleOpenRollback = (baseline) => {
    setRollbackBaselineId(baseline.id);
    setRollbackName(`Rollback to ${baseline.name}`);
    setRollbackDescription(`Schedule restored from baseline: ${baseline.name}`);
    setRollbackDialogOpen(true);
  };
  
  // Execute rollback
  const handleRollback = async () => {
    if (!rollbackBaselineId || !rollbackName.trim()) return;
    
    setRollingBack(true);
    
    try {
      await api.post('/baselines/rollback', {
        baseline_id: rollbackBaselineId,
        new_baseline_name: rollbackName.trim(),
        description: rollbackDescription.trim() || null
      });
      
      toast.success('Schedule rolled back successfully');
      setRollbackDialogOpen(false);
      setRollbackBaselineId(null);
      setRollbackName('');
      setRollbackDescription('');
      loadBaselines();
    } catch (err) {
      console.error('Failed to rollback:', err);
      toast.error(apiErrorMessage(err, 'Failed to rollback schedule'));
    } finally {
      setRollingBack(false);
    }
  };
  
  // Check if primary baseline exists
  const hasPrimaryBaseline = baselines.some(b => b.baseline_type === 'primary');
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };
  
  // Get variance badge
  const getVarianceBadge = (status) => {
    switch (status) {
      case 'ahead':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Ahead</Badge>;
      case 'behind':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Behind</Badge>;
      case 'added':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Added</Badge>;
      case 'removed':
        return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Removed</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">On Track</Badge>;
    }
  };
  
  // Get change type badge
  const getChangeTypeBadge = (changeType) => {
    switch (changeType) {
      case 'added':
        return <Badge className="bg-green-100 text-green-700 text-[10px]">+Added</Badge>;
      case 'removed':
        return <Badge className="bg-red-100 text-red-700 text-[10px]">-Removed</Badge>;
      case 'modified':
        return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Modified</Badge>;
      default:
        return null;
    }
  };
  
  // No schedule selected
  if (!selectedSchedule) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="baselines-page">
        <GitCompare className="h-12 w-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-medium text-slate-900 mb-2">No Schedule Selected</h2>
        <p className="text-sm text-slate-500">Select a project and schedule to manage baselines</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="baselines-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Baselines & Version Control
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage immutable schedule snapshots for {selectedSchedule.name}
          </p>
        </div>
        
        {can(Action.MANAGE_SCHEDULES) && (
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              className="h-9 gap-2"
              onClick={handleLoadPublishPreview}
              disabled={loadingPublishPreview}
            >
              {loadingPublishPreview ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Review & Publish Changes
            </Button>
            <Button 
              className="h-9 bg-slate-900 text-white hover:bg-slate-800 font-medium gap-2"
              onClick={() => setCreateDialogOpen(true)}
              data-testid="create-baseline-button"
            >
              <Plus className="h-4 w-4" />
              Create Baseline
            </Button>
          </div>
        )}
      </div>

      {/* Immutability Notice */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Immutable Baselines</AlertTitle>
        <AlertDescription className="text-blue-700">
          All baselines are read-only once created. They capture a frozen snapshot of your schedule
          that cannot be edited. Use &ldquo;Review &amp; Publish Changes&rdquo; to create a new baseline with current changes.
        </AlertDescription>
      </Alert>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Comparison Controls */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Compare Baseline
              </p>
              <Select 
                value={selectedBaselineId || ""} 
                onValueChange={(value) => {
                  setSelectedBaselineId(value);
                  setCompareToBaselineId(null);
                }}
                disabled={baselines.length === 0}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={baselines.length === 0 ? "No baselines available" : "Select baseline..."} />
                </SelectTrigger>
                <SelectContent>
                  {baselines.map((bl) => (
                    <SelectItem key={bl.id} value={bl.id}>
                      <span className="flex items-center gap-2">
                        <Lock className="h-3 w-3 text-slate-400" />
                        {bl.name} {bl.baseline_type === 'primary' && '(Primary)'}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Against
              </p>
              <Select 
                value={compareToBaselineId || "current"} 
                onValueChange={(value) => setCompareToBaselineId(value === "current" ? null : value)}
                disabled={!selectedBaselineId}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">
                    <span className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-blue-500" />
                      Current Schedule (Live)
                    </span>
                  </SelectItem>
                  {baselines
                    .filter(bl => bl.id !== selectedBaselineId)
                    .map((bl) => (
                      <SelectItem key={bl.id} value={bl.id}>
                        <span className="flex items-center gap-2">
                          <Lock className="h-3 w-3 text-slate-400" />
                          {bl.name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="default"
                size="sm"
                className="h-9"
                onClick={() => compareToBaselineId ? handleCompareBaselines() : handleCompareWithCurrent(selectedBaselineId)}
                disabled={!selectedBaselineId || comparing}
              >
                {comparing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitCompare className="h-4 w-4 mr-1" />}
                Compare
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-2" disabled>
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Baseline List - 2 columns */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <History className="h-4 w-4" />
                Version History
              </CardTitle>
              <Badge variant="outline" className="text-[10px]">
                {baselines.length} version{baselines.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : baselines.length > 0 ? (
              <div className="space-y-3">
                {baselines.map((baseline, index) => (
                  <div 
                    key={baseline.id} 
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-colors
                      ${selectedBaselineId === baseline.id 
                        ? 'border-blue-300 bg-blue-50' 
                        : 'border-slate-100 bg-slate-50/50 hover:bg-slate-50'}`}
                  >
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center relative
                      ${baseline.baseline_type === 'primary' ? 'bg-amber-100' : 'bg-slate-200'}`}>
                      <GitCompare className={`h-5 w-5 ${baseline.baseline_type === 'primary' ? 'text-amber-600' : 'text-slate-500'}`} />
                      <Lock className="absolute -bottom-1 -right-1 h-3.5 w-3.5 text-slate-500 bg-white rounded-full p-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">{baseline.name}</span>
                        {baseline.baseline_type === 'primary' && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">Primary</Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <Lock className="h-2.5 w-2.5" />
                          v{baseline.version || index + 1}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(baseline.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {baseline.created_by_name}
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {baseline.activity_count} activities
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {can(Action.MANAGE_SCHEDULES) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-slate-500 hover:text-slate-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRollback(baseline);
                          }}
                          title="Rollback to this version"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => handleCompareWithCurrent(baseline.id)}
                      >
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 border-t border-slate-100 mt-4">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <GitCompare className="h-6 w-6 text-slate-300" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">No baselines saved</p>
                <p className="text-xs text-slate-400 text-center max-w-xs mb-3">
                  Create a baseline to capture an immutable schedule snapshot
                </p>
                {can(Action.MANAGE_SCHEDULES) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Create First Baseline
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Variance Summary */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900">
              Variance Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {comparison ? (
              <div className="space-y-3">
                {comparison.compare_to_name && (
                  <div className="p-2 bg-slate-100 rounded text-xs text-slate-600 mb-3">
                    Comparing: <strong>{comparison.baseline_name}</strong> → <strong>{comparison.compare_to_name}</strong>
                  </div>
                )}
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-slate-600">Ahead</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">
                    {comparison.activities_ahead} activities
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">On Track</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">
                    {comparison.activities_on_track} activities
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-slate-600">Behind</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">
                    {comparison.activities_behind} activities
                  </span>
                </div>
                
                {/* Additional stats for baseline-to-baseline */}
                {(comparison.activities_added > 0 || comparison.activities_removed > 0) && (
                  <>
                    <div className="h-px bg-slate-200 my-2" />
                    {comparison.activities_added > 0 && (
                      <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50">
                        <div className="flex items-center gap-2">
                          <Plus className="h-4 w-4 text-blue-500" />
                          <span className="text-sm text-slate-600">Added</span>
                        </div>
                        <span className="text-sm font-medium text-slate-900 tabular-nums">
                          {comparison.activities_added} activities
                        </span>
                      </div>
                    )}
                    {comparison.activities_removed > 0 && (
                      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-100">
                        <div className="flex items-center gap-2">
                          <Minus className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Removed</span>
                        </div>
                        <span className="text-sm font-medium text-slate-900 tabular-nums">
                          {comparison.activities_removed} activities
                        </span>
                      </div>
                    )}
                  </>
                )}
                
                {comparison.net_schedule_impact_days !== null && (
                  <div className="mt-3 p-3 bg-slate-900 text-white rounded-lg">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Net Schedule Impact</p>
                    <p className={`text-lg font-semibold ${
                      comparison.net_schedule_impact_days > 0 ? 'text-red-400' :
                      comparison.net_schedule_impact_days < 0 ? 'text-green-400' : 'text-white'
                    }`}>
                      {comparison.net_schedule_impact_days > 0 ? '+' : ''}{comparison.net_schedule_impact_days} days
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-slate-600">Ahead</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">— activities</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">On Track</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">— activities</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-slate-600">Behind</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900 tabular-nums">— activities</span>
                </div>
              </div>
            )}

            {!comparison && (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs text-slate-500">
                <Info className="h-3 w-3" />
                Select a baseline to view variance
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Variance Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-slate-900">
            Activity Variance Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table className="min-w-[720px] sm:min-w-0">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Activity
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Change
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Baseline Start
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Start
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Baseline Finish
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Finish
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Variance (Days)
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison && comparison.activities.length > 0 ? (
                comparison.activities.map((act) => (
                  <TableRow key={act.activity_id} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div>
                        <span className="font-mono text-xs text-slate-500">{act.activity_code}</span>
                        <span className="ml-2 text-sm text-slate-900">{act.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getChangeTypeBadge(act.change_type)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.baseline_start)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.current_start)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.baseline_finish)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.current_finish)}</TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium tabular-nums ${
                        act.finish_variance_days > 0 ? 'text-red-600' :
                        act.finish_variance_days < 0 ? 'text-green-600' : 'text-slate-600'
                      }`}>
                        {act.finish_variance_days != null ? (
                          act.finish_variance_days > 0 ? `+${act.finish_variance_days}` : act.finish_variance_days
                        ) : '—'}
                      </span>
                    </TableCell>
                    <TableCell>{getVarianceBadge(act.variance_status)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    {comparing ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading comparison...
                      </div>
                    ) : (
                      'Select a baseline to view variance details'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Baseline Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Create Baseline
            </DialogTitle>
            <DialogDescription>
              Capture an immutable snapshot of the current schedule. Once created, this baseline cannot be edited.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="baseline-name">Baseline Name</Label>
              <Input
                id="baseline-name"
                placeholder="e.g., BL-1 Initial Baseline"
                value={newBaseline.name}
                onChange={(e) => setNewBaseline({ ...newBaseline, name: e.target.value })}
                className="h-9"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="baseline-type">Baseline Type</Label>
              <Select
                value={newBaseline.baseline_type}
                onValueChange={(value) => setNewBaseline({ ...newBaseline, baseline_type: value })}
                disabled={hasPrimaryBaseline && newBaseline.baseline_type === 'primary'}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary" disabled={hasPrimaryBaseline}>
                    Primary {hasPrimaryBaseline && '(Already exists)'}
                  </SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {hasPrimaryBaseline 
                  ? 'A primary baseline already exists. Create a secondary baseline.'
                  : 'Primary baseline is the main reference point. Secondary baselines are for additional snapshots.'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="baseline-description">Description (Optional)</Label>
              <Textarea
                id="baseline-description"
                placeholder="Notes about this baseline..."
                value={newBaseline.description}
                onChange={(e) => setNewBaseline({ ...newBaseline, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBaseline}
              disabled={creating || !newBaseline.name.trim()}
              className="gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              <Lock className="h-3.5 w-3.5" />
              Create Baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Publish Changes
            </DialogTitle>
            <DialogDescription>
              Review changes before publishing as a new baseline version.
            </DialogDescription>
          </DialogHeader>
          
          {publishPreview && (
            <div className="space-y-4 py-4">
              {/* Change Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-semibold text-green-700">{publishPreview.activities_added}</p>
                  <p className="text-[10px] text-green-600 uppercase">Added</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-semibold text-amber-700">{publishPreview.activities_modified}</p>
                  <p className="text-[10px] text-amber-600 uppercase">Modified</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-semibold text-red-700">{publishPreview.activities_removed}</p>
                  <p className="text-[10px] text-red-600 uppercase">Removed</p>
                </div>
              </div>
              
              {/* Impact Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration changes:</span>
                  <span className="font-medium">{publishPreview.duration_changes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date changes:</span>
                  <span className="font-medium">{publishPreview.date_changes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Relationship changes:</span>
                  <span className="font-medium">{publishPreview.relationship_changes}</span>
                </div>
                {publishPreview.critical_path_changed && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    Critical path has changed
                  </div>
                )}
                {publishPreview.net_schedule_impact_days !== null && (
                  <div className={`flex justify-between p-2 rounded ${
                    publishPreview.net_schedule_impact_days > 0 ? 'bg-red-50 text-red-700' :
                    publishPreview.net_schedule_impact_days < 0 ? 'bg-green-50 text-green-700' : 'bg-slate-50'
                  }`}>
                    <span>Net schedule impact:</span>
                    <span className="font-semibold">
                      {publishPreview.net_schedule_impact_days > 0 ? '+' : ''}{publishPreview.net_schedule_impact_days} days
                    </span>
                  </div>
                )}
              </div>
              
              {publishPreview.total_activities_changed === 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>No changes detected since the last baseline.</AlertDescription>
                </Alert>
              )}
              
              <div className="h-px bg-slate-200" />
              
              {/* Baseline Details */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="publish-name">Baseline Name</Label>
                  <Input
                    id="publish-name"
                    value={publishName}
                    onChange={(e) => setPublishName(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="publish-description">Description (Optional)</Label>
                  <Textarea
                    id="publish-description"
                    placeholder="Notes about these changes..."
                    value={publishDescription}
                    onChange={(e) => setPublishDescription(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPublishDialogOpen(false)}
              disabled={publishing}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePublish}
              disabled={publishing || !publishName.trim() || publishPreview?.total_activities_changed === 0}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
              <CheckCircle className="h-4 w-4" />
              Confirm & Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={rollbackDialogOpen} onOpenChange={setRollbackDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RotateCcw className="h-5 w-5" />
              Rollback Schedule
            </DialogTitle>
            <DialogDescription>
              This will restore the schedule to a previous baseline state. A new baseline will be created to preserve history.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Warning</AlertTitle>
              <AlertDescription className="text-amber-700">
                Current schedule changes will be replaced with the selected baseline&apos;s data. 
                This action cannot be undone, but a new baseline will be created.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label htmlFor="rollback-name">New Baseline Name</Label>
              <Input
                id="rollback-name"
                value={rollbackName}
                onChange={(e) => setRollbackName(e.target.value)}
                className="h-9"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rollback-description">Description (Optional)</Label>
              <Textarea
                id="rollback-description"
                value={rollbackDescription}
                onChange={(e) => setRollbackDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRollbackDialogOpen(false)}
              disabled={rollingBack}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRollback}
              disabled={rollingBack || !rollbackName.trim()}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
            >
              {rollingBack && <Loader2 className="h-4 w-4 animate-spin" />}
              <RotateCcw className="h-4 w-4" />
              Confirm Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BaselinesPage;
